import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SalesLayout } from '@/components/SalesLayout';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  
  return {
    from: formatDate(firstDay),
    to: formatDate(lastDay)
  };
};

// Format date for display
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  } catch { return dateStr; }
};

// Get days until expiry
const getDaysUntilExpiry = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = new Date(dateStr);
  expiryDate.setHours(0, 0, 0, 0);
  const diffTime = expiryDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get expiry status badge
const getExpiryBadge = (days) => {
  if (days === null) return { text: 'No Date', color: 'bg-gray-100 text-gray-600' };
  if (days < 0) return { text: `Expired ${Math.abs(days)}d ago`, color: 'bg-red-100 text-red-700' };
  if (days === 0) return { text: 'Expires Today!', color: 'bg-red-500 text-white' };
  if (days <= 7) return { text: `${days} days left`, color: 'bg-orange-100 text-orange-700' };
  if (days <= 30) return { text: `${days} days left`, color: 'bg-yellow-100 text-yellow-700' };
  return { text: `${days} days left`, color: 'bg-green-100 text-green-700' };
};

function InsuranceExpiryReport({ user, onLogout }) {
  const [bookings, setBookings] = React.useState([]);
  const [companyDetails, setCompanyDetails] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('own_damage'); // 'own_damage' or 'third_party'
  
  const defaultRange = getCurrentMonthRange();
  const [odDateFrom, setOdDateFrom] = React.useState(defaultRange.from);
  const [odDateTo, setOdDateTo] = React.useState(defaultRange.to);
  const [tpDateFrom, setTpDateFrom] = React.useState(defaultRange.from);
  const [tpDateTo, setTpDateTo] = React.useState(defaultRange.to);
  
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedBookings, setSelectedBookings] = React.useState([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, companyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings?limit=10000`),
        axios.get(`${BACKEND_URL}/api/masters?type=company`)
      ]);
      setBookings(bookingsRes.data || []);
      // Get company details
      const companyData = companyRes.data?.[0] || {};
      setCompanyDetails(companyData);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  // Filter bookings based on active tab and date range
  const filteredBookings = React.useMemo(() => {
    let filtered = [...bookings];
    
    if (activeTab === 'own_damage') {
      // Filter by Own Damage Expiry Date
      filtered = filtered.filter(b => {
        if (!b.own_damage_expiry_date) return false;
        const expiryDate = new Date(b.own_damage_expiry_date);
        const from = odDateFrom ? new Date(odDateFrom) : null;
        const to = odDateTo ? new Date(odDateTo) : null;
        if (to) to.setHours(23, 59, 59);
        
        if (from && expiryDate < from) return false;
        if (to && expiryDate > to) return false;
        return true;
      });
    } else {
      // Filter by Third Party Expiry Date
      filtered = filtered.filter(b => {
        if (!b.third_party_expiry_date) return false;
        const expiryDate = new Date(b.third_party_expiry_date);
        const from = tpDateFrom ? new Date(tpDateFrom) : null;
        const to = tpDateTo ? new Date(tpDateTo) : null;
        if (to) to.setHours(23, 59, 59);
        
        if (from && expiryDate < from) return false;
        if (to && expiryDate > to) return false;
        return true;
      });
    }

    // Search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(b =>
        b.booking_no?.toString().includes(search) ||
        b.customer_first_name?.toLowerCase().includes(search) ||
        b.customer_last_name?.toLowerCase().includes(search) ||
        b.mobile_no_1?.includes(search) ||
        b.registration_number?.toLowerCase().includes(search) ||
        b.chassis_no?.toLowerCase().includes(search) ||
        b.policy_no?.toLowerCase().includes(search)
      );
    }

    // Sort by expiry date (earliest first)
    const dateField = activeTab === 'own_damage' ? 'own_damage_expiry_date' : 'third_party_expiry_date';
    filtered.sort((a, b) => {
      const dateA = a[dateField] ? new Date(a[dateField]) : new Date('9999-12-31');
      const dateB = b[dateField] ? new Date(b[dateField]) : new Date('9999-12-31');
      return dateA - dateB;
    });

    return filtered;
  }, [bookings, activeTab, odDateFrom, odDateTo, tpDateFrom, tpDateTo, searchValue]);

  // Metrics
  const metrics = React.useMemo(() => {
    const dateField = activeTab === 'own_damage' ? 'own_damage_expiry_date' : 'third_party_expiry_date';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let expired = 0;
    let expiringIn7Days = 0;
    let expiringIn30Days = 0;
    let safe = 0;

    filteredBookings.forEach(b => {
      const days = getDaysUntilExpiry(b[dateField]);
      if (days === null) return;
      if (days < 0) expired++;
      else if (days <= 7) expiringIn7Days++;
      else if (days <= 30) expiringIn30Days++;
      else safe++;
    });

    return { expired, expiringIn7Days, expiringIn30Days, safe, total: filteredBookings.length };
  }, [filteredBookings, activeTab]);

  // Selection handlers
  const handleSelectBooking = (id) => {
    setSelectedBookings(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id));
    }
  };

  // WhatsApp message template - Enhanced with Company Details
  const getWhatsAppMessage = (booking) => {
    const dateField = activeTab === 'own_damage' ? 'own_damage_expiry_date' : 'third_party_expiry_date';
    const expiryType = activeTab === 'own_damage' ? 'Own Damage' : 'Third Party';
    
    // Get company details
    const companyName = companyDetails?.company_name || 'Your Dealer';
    const companyPhone = companyDetails?.phone_1 || companyDetails?.phone_2 || '';
    const companyEmail = companyDetails?.email || '';
    
    // Customer name with salutation
    const customerName = `${booking.salutation || ''} ${booking.customer_first_name || 'Customer'}`.trim();
    
    return encodeURIComponent(
      `🌟 *Insurance Renewal Reminder* 🌟\n\n` +
      `Dear ${customerName},\n\n` +
      `Your policy #*${booking.policy_no || 'N/A'}* expires on *${formatDisplayDate(booking[dateField])}*.\n\n` +
      `Renew today to stay protected without any lapse!\n\n` +
      `✅ Coverage continues seamlessly\n` +
      `✅ No Claim Bonus (if there is no claim in previous year)\n` +
      `✅ Quick online process\n` +
      `✅ Road Side Assistance\n` +
      `✅ Battery Protection Cover\n` +
      `✅ Cash Less Claims\n\n` +
      `Reply *YES* to renew\n\n` +
      `Any Questions? Please WhatsApp us or mail us\n\n` +
      `*Best Regards*\n` +
      `${companyName}\n` +
      (companyPhone ? `📞 ${companyPhone}\n` : '') +
      (companyEmail ? `📧 ${companyEmail}\n` : '') +
      `🛡️ Secure & Simple`
    );
  };

  // Send WhatsApp to single customer
  const handleSendWhatsApp = (booking) => {
    const phone = booking.whatsapp_no_1 || booking.mobile_no_1;
    if (!phone) {
      toast.error('No phone number available for this customer');
      return;
    }
    const message = getWhatsAppMessage(booking);
    { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=91${phone}&text=${message}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
    toast.success(`WhatsApp opened for ${booking.customer_first_name}`);
  };

  // Send WhatsApp to selected customers
  const handleBulkWhatsApp = () => {
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }

    const selectedData = filteredBookings.filter(b => selectedBookings.includes(b.id));
    let sent = 0;
    let failed = 0;

    selectedData.forEach((booking, index) => {
      const phone = booking.whatsapp_no_1 || booking.mobile_no_1;
      if (phone) {
        setTimeout(() => {
          const message = getWhatsAppMessage(booking);
          { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=91${phone}&text=${message}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
        }, index * 1000); // Stagger opening by 1 second each
        sent++;
      } else {
        failed++;
      }
    });

    toast.success(`Opening WhatsApp for ${sent} customers${failed > 0 ? ` (${failed} without phone)` : ''}`);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const dateField = activeTab === 'own_damage' ? 'own_damage_expiry_date' : 'third_party_expiry_date';
    const expiryLabel = activeTab === 'own_damage' ? 'OD Expiry Date' : 'TP Expiry Date';
    
    const exportData = filteredBookings.map((b, idx) => ({
      'S.No': idx + 1,
      'Booking No': b.booking_no,
      'Customer Name': `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim(),
      'Mobile': b.mobile_no_1 || '-',
      'WhatsApp': b.whatsapp_no_1 || '-',
      'Vehicle': `${b.model || ''} ${b.variant || ''}`.trim(),
      'Registration No': b.registration_number || '-',
      'Policy No': b.policy_no || '-',
      'Insurance Company': b.insurance_company_name || '-',
      [expiryLabel]: formatDisplayDate(b[dateField]),
      'Days Until Expiry': getDaysUntilExpiry(b[dateField]) ?? 'N/A',
      'Status': getExpiryBadge(getDaysUntilExpiry(b[dateField])).text
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Insurance Expiry');
    XLSX.writeFile(wb, `Insurance_Expiry_${activeTab === 'own_damage' ? 'OD' : 'TP'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const dateField = activeTab === 'own_damage' ? 'own_damage_expiry_date' : 'third_party_expiry_date';

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Insurance Expiry Report">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Insurance Expiry Report">
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-800">🛡️ Insurance Expiry Report</h2>
              <p className="text-sm text-gray-500">Track and manage insurance renewals with WhatsApp automation</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                data-testid="export-excel-btn"
              >
                📊 Export Excel
              </button>
              <button
                onClick={handleBulkWhatsApp}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  selectedBookings.length > 0 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : 'bg-gray-300 text-gray-600'
                }`}
                data-testid="bulk-whatsapp-btn"
              >
                📱 Send WhatsApp ({selectedBookings.length})
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-2 shadow-sm">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('own_damage')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'own_damage' 
                  ? 'bg-orange-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="tab-own-damage"
            >
              🔶 Own Damage (OD) Expiry
            </button>
            <button
              onClick={() => setActiveTab('third_party')}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'third_party' 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="tab-third-party"
            >
              🔷 Third Party (TP) Expiry
            </button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-md">
            <p className="text-sm opacity-90">Expired</p>
            <p className="text-2xl font-bold">{metrics.expired}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl p-4 text-white shadow-md">
            <p className="text-sm opacity-90">Expiring in 7 Days</p>
            <p className="text-2xl font-bold">{metrics.expiringIn7Days}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl p-4 text-white shadow-md">
            <p className="text-sm opacity-90">Expiring in 30 Days</p>
            <p className="text-2xl font-bold">{metrics.expiringIn30Days}</p>
          </div>
          <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-xl p-4 text-white shadow-md">
            <p className="text-sm opacity-90">Safe (30+ Days)</p>
            <p className="text-2xl font-bold">{metrics.safe}</p>
          </div>
          <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl p-4 text-white shadow-md">
            <p className="text-sm opacity-90">Total Records</p>
            <p className="text-2xl font-bold">{metrics.total}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {activeTab === 'own_damage' ? 'OD' : 'TP'} Expiry From
              </label>
              <input
                type="date"
                value={activeTab === 'own_damage' ? odDateFrom : tpDateFrom}
                onChange={(e) => activeTab === 'own_damage' ? setOdDateFrom(e.target.value) : setTpDateFrom(e.target.value)}
                className="w-full p-2 border border-[#FFCAD4] rounded-lg text-sm"
                data-testid="date-from"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {activeTab === 'own_damage' ? 'OD' : 'TP'} Expiry To
              </label>
              <input
                type="date"
                value={activeTab === 'own_damage' ? odDateTo : tpDateTo}
                onChange={(e) => activeTab === 'own_damage' ? setOdDateTo(e.target.value) : setTpDateTo(e.target.value)}
                className="w-full p-2 border border-[#FFCAD4] rounded-lg text-sm"
                data-testid="date-to"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search by name, mobile, booking no, registration, policy..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full p-2 border border-[#FFCAD4] rounded-lg text-sm"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const range = getCurrentMonthRange();
                  if (activeTab === 'own_damage') {
                    setOdDateFrom(range.from);
                    setOdDateTo(range.to);
                  } else {
                    setTpDateFrom(range.from);
                    setTpDateTo(range.to);
                  }
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Reset to Current Month
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white/80 border border-[#FFCAD4]/30 rounded-xl overflow-hidden shadow-sm">
          <div className="p-3 border-b border-[#FFCAD4]/20 bg-[#FFE5D9]/20">
            <span className="text-sm text-gray-600">
              Showing {filteredBookings.length} records | 
              <strong className="ml-1">{activeTab === 'own_damage' ? 'Own Damage' : 'Third Party'} Expiry</strong>
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="insurance-expiry-table">
              <thead className="bg-[#98D8AA]/20 border-b-2 border-[#98D8AA]/40">
                <tr>
                  <th className="p-3 text-left border-r border-gray-200">
                    <input 
                      type="checkbox" 
                      checked={filteredBookings.length > 0 && selectedBookings.length === filteredBookings.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">#</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">B.No</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">Customer</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">Mobile</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">Vehicle</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">Reg No</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">Policy No</th>
                  <th className="p-3 text-left font-semibold text-gray-700 border-r border-gray-200">Insurance Co.</th>
                  <th className="p-3 text-center font-semibold text-gray-700 bg-orange-50 border-r border-gray-200">
                    {activeTab === 'own_damage' ? 'OD Expiry' : 'TP Expiry'}
                  </th>
                  <th className="p-3 text-center font-semibold text-gray-700 bg-yellow-50 border-r border-gray-200">Status</th>
                  <th className="p-3 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="p-8 text-center text-gray-500">
                      No records found for the selected date range
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking, idx) => {
                    const days = getDaysUntilExpiry(booking[dateField]);
                    const badge = getExpiryBadge(days);
                    const bgColor = idx % 2 === 0 ? 'bg-white' : 'bg-[#FFF9F5]';
                    
                    return (
                      <tr key={booking.id} className={`${bgColor} border-b border-gray-200 hover:bg-blue-50/50`}>
                        <td className="p-3 border-r border-gray-100">
                          <input 
                            type="checkbox" 
                            checked={selectedBookings.includes(booking.id)}
                            onChange={() => handleSelectBooking(booking.id)}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="p-3 text-gray-500 border-r border-gray-100">{idx + 1}</td>
                        <td className="p-3 font-medium text-blue-600 border-r border-gray-100">{booking.booking_no}</td>
                        <td className="p-3 border-r border-gray-100">
                          {booking.customer_first_name} {booking.customer_last_name || ''}
                        </td>
                        <td className="p-3 border-r border-gray-100">{booking.mobile_no_1 || '-'}</td>
                        <td className="p-3 border-r border-gray-100">{booking.model} {booking.variant}</td>
                        <td className="p-3 font-semibold text-gray-800 border-r border-gray-100">
                          {booking.registration_number || '-'}
                        </td>
                        <td className="p-3 border-r border-gray-100">{booking.policy_no || '-'}</td>
                        <td className="p-3 border-r border-gray-100">{booking.insurance_company_name || '-'}</td>
                        <td className="p-3 text-center font-medium bg-orange-50/50 border-r border-gray-100">
                          {formatDisplayDate(booking[dateField])}
                        </td>
                        <td className="p-3 text-center bg-yellow-50/50 border-r border-gray-100">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                            {badge.text}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => handleSendWhatsApp(booking)}
                            className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-xs font-medium"
                            title="Send WhatsApp Reminder"
                          >
                            📱 WhatsApp
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SalesLayout>
  );
}

export default InsuranceExpiryReport;
